import { useState, useEffect, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { updateUserProfile } from '@/lib/supabase/user';
import { Loader2 } from 'lucide-react';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { AvatarUpload } from './AvatarUpload';

const MAX_NAME_LENGTH = 100;
const MAX_JOB_TITLE_LENGTH = 100;

interface FormErrors {
  name?: string;
  jobTitle?: string;
}

export function AccountSettings() {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.user_metadata?.name || '',
    jobTitle: user?.user_metadata?.job_title || '',
  });
  const [initialData, setInitialData] = useState({
    name: user?.user_metadata?.name || '',
    jobTitle: user?.user_metadata?.job_title || '',
  });

  useEffect(() => {
    if (user?.user_metadata) {
      const newData = {
        name: user.user_metadata.name || '',
        jobTitle: user.user_metadata.job_title || '',
      };
      setFormData(newData);
      setInitialData(newData);
    }
  }, [user?.user_metadata]);

  const errors = useMemo<FormErrors>(() => {
    const errs: FormErrors = {};
    if (!formData.name.trim()) {
      errs.name = 'Name is required';
    } else if (formData.name.length > MAX_NAME_LENGTH) {
      errs.name = `Name must be ${MAX_NAME_LENGTH} characters or less`;
    }
    if (formData.jobTitle.length > MAX_JOB_TITLE_LENGTH) {
      errs.jobTitle = `Job title must be ${MAX_JOB_TITLE_LENGTH} characters or less`;
    }
    return errs;
  }, [formData.name, formData.jobTitle]);

  const isValid = Object.keys(errors).length === 0;
  const isDirty = formData.name !== initialData.name || formData.jobTitle !== initialData.jobTitle;

  const handleSave = async () => {
    if (!isValid) return;

    setIsLoading(true);
    try {
      await updateUserProfile({
        name: formData.name.trim(),
        job_title: formData.jobTitle.trim(),
      });

      const newData = {
        name: formData.name.trim(),
        jobTitle: formData.jobTitle.trim(),
      };
      setFormData(newData);
      setInitialData(newData);

      if (refreshUser) {
        await refreshUser();
      }

      toast({
        title: 'Success',
        description: 'Your profile has been updated',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update profile',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormData(initialData);
  };

  const handleAvatarSuccess = async () => {
    if (refreshUser) {
      await refreshUser();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Account Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <AvatarUpload
          avatarUrl={user?.user_metadata?.avatar_url}
          name={formData.name || user?.email || 'User'}
          onSuccess={handleAvatarSuccess}
        />

        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              maxLength={MAX_NAME_LENGTH + 10}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'name-error' : undefined}
            />
            {errors.name && (
              <p id="name-error" className="text-xs text-destructive">
                {errors.name}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {formData.name.length}/{MAX_NAME_LENGTH} characters
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={user?.email || ''}
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              Email cannot be changed
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="jobTitle">Job Title</Label>
            <Input
              id="jobTitle"
              value={formData.jobTitle}
              onChange={(e) => setFormData(prev => ({ ...prev, jobTitle: e.target.value }))}
              maxLength={MAX_JOB_TITLE_LENGTH + 10}
              aria-invalid={!!errors.jobTitle}
              aria-describedby={errors.jobTitle ? 'jobTitle-error' : undefined}
            />
            {errors.jobTitle && (
              <p id="jobTitle-error" className="text-xs text-destructive">
                {errors.jobTitle}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {formData.jobTitle.length}/{MAX_JOB_TITLE_LENGTH} characters
            </p>
          </div>
        </div>

        <div className="flex gap-4">
          <Button onClick={handleSave} disabled={isLoading || !isValid || !isDirty}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
          {isDirty && (
            <Button variant="outline" onClick={handleReset}>
              Reset
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setShowPasswordDialog(true)}
          >
            Change Password
          </Button>
        </div>
      </CardContent>

      <ChangePasswordDialog
        open={showPasswordDialog}
        onOpenChange={setShowPasswordDialog}
      />
    </Card>
  );
}
